import React, { useState, useRef, useEffect } from "react";
import FreeToolsLayout from "./FreeToolsLayout";
import sparkleWhite from "~/images/sparkle-white.svg";
import sparklePurple from "~/images/sparkle-purple.svg";
import { generateInstagramCaption } from "../../api/freeToolsApis";
import CopyText from "../shared/CopyText";
import GenerateResults from "../shared/GenerateResults";
const InstagramCaptionGenerator = () => {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("casual");
  const [hashtagsCount, setHashtagsCount] = useState("5");
  const [includeEmojis, setIncludeEmojis] = useState(false);
  const [caption, setCaption] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const resultRef = useRef(null);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError("Please describe your post content");
      return;
    }
    setCaption("");
    setLoading(true);
    setError("");

    try {
      const response = await generateInstagramCaption({
        topic,
        tone,
        hashtags_count: hashtagsCount,
        include_emojis: includeEmojis,
      });
      if (response.status === 200) {
        setCaption(response.data.caption);
        setLoading(false);
      } else {
        throw new Error(response.data.error || "Failed to generate caption");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getButtonText = () => {
    if (loading) return "Generating...";
    if (caption) return "Generate Again";
    return "Generate";
  };
  useEffect(() => {
    if (caption && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [caption]);
  return (
    <FreeToolsLayout mainHeading="AI Instagram Caption Generator" subHeading="Create engaging Instagram captions that drive likes, comments, and shares using AI">
      <div className="bg-white/70 rounded-[20px] py-8 px-[26px] shadow-md shadow-[#6248fe1f] relative min-h-[200px]">
        <div className="mb-4">
          <label className="text-[#3f3f3f] text-base font-bold mb-1">What's your post about?</label>
          <p className="text-[#696969] text-sm mb-2">Describe your instagram post content or topic</p>
          <textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            id="inputText"
            className="w-full pr-16 bg-transparent rounded-lg border-[#e9e4ff] outline-none focus:border-[#e9e4ff] resize-none text-sm min-h-[120px] text-black placeholder-[#707070] focus:ring-0"
          ></textarea>
        </div>
        <div className="mb-4">
          <label className="text-[#3f3f3f] text-base font-bold mb-1">Tone of Voice</label>
          <div class="mt-2">
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              id=""
              name="tone"
              class="bg-transparent block w-full rounded-md py-1.5 text-gray-900 shadow-sm sm:text-sm sm:leading-6 border-[#e9e4ff] outline-none focus:border-[#e9e4ff] focus:ring-[#6248fe]"
            >
              <option value="casual">Casual & Friendly</option>
              <option value="professional">Professional & Formal</option>
              <option value="funny">Funny & Humorous</option>
              <option value="inspirational">Inspirational & Motivational</option>
              <option value="educational">Educational & Informative</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label class="text-[#3f3f3f] text-base font-bold mb-1">Number of Hashtags</label>
          <div class="mt-2">
            <select
              value={hashtagsCount}
              onChange={(e) => setHashtagsCount(e.target.value)}
              id=""
              name="hashtags_count"
              class="bg-transparent block w-full rounded-md py-1.5 text-gray-900 shadow-sm sm:text-sm sm:leading-6 border-[#e9e4ff] outline-none focus:border-[#e9e4ff] focus:ring-[#6248fe]"
            >
              <option value="5">5 hashtags</option>
              <option value="10">10 hashtags</option>
              <option value="15">15 hashtags</option>
              <option value="20">20 hashtags</option>
              <option value="30">30 hashtags</option>
            </select>
          </div>
        </div>

        <div class="relative flex items-start">
          <div class="flex h-6 items-center">
            <input
              checked={includeEmojis}
              onChange={(e) => setIncludeEmojis(e.target.checked)}
              id=""
              name="include_emojis"
              type="checkbox"
              class="bg-transparent h-4 w-4 rounded border-[#e9e4ff] text-[#6248fe] focus:ring-[#6248fe]"
            />
          </div>
          <div class="ml-3">
            <label for="include_emojis" class="text-[#3f3f3f] text-base font-bold mb-1">
              Include Emojis
            </label>
            <p class="text-[#696969] text-sm mb-2">Add relevant emojis to make your caption more engaging</p>
          </div>
        </div>
        <GenerateResults loading={loading} handleGenerate={handleGenerate} text={getButtonText()} />
      </div>
      {error && <div className="text-red-600 text-sm px-2">{error}</div>}
      {caption && (
        <div ref={resultRef}>
          <div className="flex items-center justify-center mb-4">
            <img src={sparklePurple} />
          </div>
          <div className="bg-white/70 rounded-[20px] p-[26px] shadow-md shadow-[#6248fe1f] relative min-h-[200px]">
            <div className="mt-6 space-y-4 bg-transparent p-4 rounded-md border border-[#e9e4ff]">
              <h4 className="font-bold">Generated Caption</h4>
              <p id="main-text" className="text-gray-800">
                {caption.main_text}
              </p>

              <div id="hashtags" className="flex flex-wrap gap-2">
                {caption.hashtags.map((hashtag, i) => (
                  <span key={i} className="bg-[#6248fe1a] border border-[#6248fe61] p-2 text-sm text-[#6248fe] rounded-md">
                    {hashtag}
                  </span>
                ))}
              </div>

              <div className="flex justify-end">
                <CopyText description={`${caption.main_text}\n\n${caption.hashtags.join(" ")}`} />
              </div>
            </div>

            {includeEmojis && caption.emojis_suggestions?.length > 0 && (
              <div className="mt-6 space-y-4 bg-transparent p-4 rounded-lg border border-[#e9e4ff]">
                <div>
                  <h4 className="font-bold">Suggested Emojis</h4>
                  <div id="emojis" className="flex gap-2 flex-wrap">
                    {caption.emojis_suggestions.map((emoji, i) => (
                      <span
                        key={i}
                        className="text-xl cursor-pointer hover:scale-110 transition-transform"
                        title="Click to copy"
                        onClick={() => navigator.clipboard.writeText(emoji)}
                      >
                        {emoji}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 space-y-4 bg-transparent p-4 rounded-lg border border-[#e9e4ff]">
              <h4 className="font-bold">Engagement Tips</h4>
              <ul id="engagement-tips" className="list-disc ml-5 text-gray-600">
                {caption.engagement_tips.map((tip, i) => (
                  <li key={i} className="text-gray-900">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 space-y-4 bg-transparent p-4 rounded-lg border border-[#e9e4ff]">
              <h4 className="font-bold">Best Posting Times</h4>
              <ul id="posting-times" className="list-disc ml-5 text-gray-600">
                {caption.best_posting_times.map((time, i) => (
                  <li key={i} className="text-gray-900">
                    {time}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </FreeToolsLayout>
  );
};

export default InstagramCaptionGenerator;
